import React, { useCallback, useState } from 'react';
import { MCButton, MCText, PopoverMenu, View } from 'src/components/basic';
import { remCss, scssVariables as variables } from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import model from 'src/model';
import { MCDatePicker } from 'src/components/complex';
import moment from 'moment';
import constants from 'src/constants';

const width = 320;

const accountStates = constants.general.accountStates;

const StateItem = ({ state }) => {
  let color = 'primary';
  switch (state) {
    case accountStates.not_available:
      color = 'quaternary';
      break;
    case accountStates.available:
      color = 'primary';
      break;
  }
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: remCss(10), flex: 1 }}>
      <div
        className={`mc-button circle ${color} tiny dark`}
        style={{ height: remCss(10), width: remCss(10) }}
      />
      <MCText label={LANG_CONST[state] || ''} size={16} />
    </View>
  );
};

export const AccountStateSelect = ({ template = 1, showDuration = true }) => {
  model.use();

  const accountState = model.state.user?.accountState || {};
  const accountStateStartDate = accountState.startDate || null;
  const accountStateEndDate = accountState.endDate || null;

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDate, setShowDate] = useState(false);

  const openSetDates = useCallback(() => {
    if (accountStateStartDate !== startDate) {
      setStartDate(accountStateStartDate ? moment(accountStateStartDate) : null);
    }
    if (accountStateEndDate !== endDate) {
      setEndDate(accountStateEndDate ? moment(accountStateEndDate) : null);
    }
    setShowDate((o) => !o);
  }, [accountStateEndDate, accountStateStartDate, endDate, startDate]);

  const onChange = useCallback(
    (v) => {
      if (v.isSame(startDate, 'date')) {
        setStartDate(endDate);
        setEndDate(null);
      } else if (v.isSame(endDate, 'date')) {
        setEndDate(null);
      } else if (!endDate && !startDate) {
        setStartDate(v);
      } else {
        let date;
        if (endDate && startDate) {
          date = v.isBefore(startDate, 'date') ? endDate : startDate;
        } else if (endDate) {
          date = endDate;
        } else {
          date = startDate;
        }
        if (date.isBefore(v, 'date')) {
          setStartDate(date);
          setEndDate(v);
        } else {
          setStartDate(v);
          setEndDate(date);
        }
      }
    },
    [startDate, endDate]
  );

  const duration = accountStateStartDate && showDuration && (
    <MCText
      size={17}
      lineHeight={17}
      textLight
      style={{ paddingTop: 2 }}
      label={`${moment(accountStateStartDate).format('DD/MM/YYYY')}${
        accountStateEndDate ? moment(accountStateEndDate).format('[ - ]DD/MM/YYYY') : ''
      }`}
    />
  );

  return (
    <PopoverMenu
      placement={'bottom-end'}
      triggerOffset={0}
      autoToggle={false}
      onClose={() => {
        setShowDate(false);
      }}
      popupMenuStyle={{
        width: remCss(width),
        backgroundColor: 'white',
        borderBottomLeftRadius: remCss(5),
        borderBottomRightRadius: remCss(5),
        borderTop: 'none'
      }}
      menu={(close, isOpen) => (
        <View style={{ width: remCss(width) }}>
          <MCButton
            base
            medium
            data-testid={'account-state-available-button'}
            onClick={() => {
              model.saveAccountState({ state: accountStates.available });
              close();
            }}
            light={accountState?.state === accountStates.available}
            hover={accountState?.state === accountStates.not_available}
            style={{ width: remCss(width) }}
          >
            <StateItem state={accountStates.available} />
          </MCButton>
          <MCButton
            style={{ width: remCss(width) }}
            base
            medium
            data-testid={'account-state-not-available-button'}
            onClick={() => {
              setShowDate(false);
              model.saveAccountState({ state: accountStates.not_available });
            }}
            light={accountState?.state === accountStates.not_available}
            hover={accountState?.state !== accountStates.not_available}
          >
            <StateItem state={accountStates.not_available} />
          </MCButton>
          {accountState?.state === accountStates.not_available && isOpen && (
            <View
              style={{
                flexDirection: 'row',
                padding: remCss(15),
                gap: remCss(15),
                alignItems: 'center'
              }}
            >
              <MCButton
                primary
                medium
                tiny
                radius
                dark
                data-testid={'account-state-set-dates-button'}
                label={LANG_CONST.buttons.set_dates}
                style={{ paddingHorizontal: remCss(7), paddingVertical: remCss(5) }}
                onClick={openSetDates}
              />
              {duration}
            </View>
          )}
          {accountState?.state === accountStates.not_available && showDate && (
            <View>
              <MCDatePicker
                style={{ padding: `0 ${remCss(15)}px` }}
                minDate={moment()}
                onChange={onChange}
                current={startDate?.format('YYYY-MM-DD')}
                onClassNamesOfCell={(date, classNames) => {
                  const index = classNames?.indexOf('selected');
                  if (index > -1) {
                    classNames?.splice(index, 1);
                  }
                  if (startDate && date.isSame(startDate, 'date')) {
                    classNames.push('available');
                  }
                  if (endDate && date.isSame(endDate, 'date')) {
                    classNames.push('available');
                  }
                  if (endDate && startDate && date.isBetween(startDate, endDate, 'date')) {
                    classNames.push('available');
                  }
                }}
              />
              <View
                style={{
                  flexDirection: 'row',
                  paddingRight: remCss(15),
                  paddingBottom: remCss(15),
                  justifyContent: 'flex-end'
                }}
              >
                <MCButton
                  primary
                  tabIndex={-1}
                  data-testid={'account-state-save-dates-button'}
                  style={{ paddingHorizontal: remCss(7), paddingVertical: remCss(5) }}
                  label={LANG_CONST.buttons.save}
                  dark
                  onClick={() => {
                    model.saveAccountState({
                      state: accountStates.not_available,
                      startDate: startDate?.valueOf(),
                      endDate: endDate?.valueOf()
                    });
                    close();
                  }}
                  radius
                  tiny
                />
              </View>
            </View>
          )}
        </View>
      )}
    >
      {(isOpen, toggle, triggerProps) =>
        template === 2
          ? template2Button(isOpen, toggle, triggerProps, accountState, duration)
          : template1Button(isOpen, toggle, triggerProps, accountState, duration)
      }
    </PopoverMenu>
  );
};

const template2Button = (isOpen, toggle, triggerProps, accountState, duration) => {
  return (
    <>
      <MCButton
        type={'button'}
        primary
        radius
        light
        tiny
        data-testid={'account-state-trigger'}
        onClick={toggle}
        {...triggerProps}
        style={{ width: remCss(168), padding: remCss(5) }}
      >
        <MCText
          label={LANG_CONST[accountState?.state || accountStates.available] || ''}
          size={18}
          primary
          flex
        />
        <span
          className={'mc-rotate ' + (isOpen ? 'mc-rotate-180' : '')}
          style={{ height: remCss(32), width: remCss(32) }}
        >
          <SVGIcon
            iconname={BASE_ICONS.DOWN}
            width={32}
            padding={5}
            fill={variables['colors-mc-primary']}
          />
        </span>
      </MCButton>
      {duration}
    </>
  );
};

const template1Button = (isOpen, toggle, triggerProps, accountState, duration) => {
  return (
    <>
      <MCButton
        type={'button'}
        primary
        radius
        tiny
        data-testid={'account-state-trigger'}
        onClick={toggle}
        {...triggerProps}
        style={{ width: remCss(168), paddingLeft: remCss(8) }}
      >
        <StateItem state={accountState?.state || accountStates.available} />
        <span
          className={'mc-rotate ' + (isOpen ? 'mc-rotate-180' : '')}
          style={{ height: remCss(32), width: remCss(32) }}
        >
          <SVGIcon
            iconname={BASE_ICONS.DOWN}
            width={32}
            padding={5}
            fill={variables['colors-mc-text-light']}
          />
        </span>
      </MCButton>
      {duration}
    </>
  );
};
